import { useEffect, useMemo, useState } from "react";
import { PlotItem, type IPlotItem } from "../../containers/PlotsContainer";
import { FullscreenPanelCard } from "../FullscreenPanelCard";

interface PlotViewerCardProps {
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  plots: IPlotItem[];
  collapsedClassName?: string;
}

interface PlotGroup {
  count: number;
  plot: IPlotItem;
  plotType: string;
}

const formatPlotType = (plotType: string) =>
  plotType
    .split("_")
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const usePlotSelection = (plots: IPlotItem[]) => {
  const groupedPlots = useMemo(() => {
    if (!plots?.length) return new Map<string, IPlotItem[]>();

    return plots.reduce((acc, plot) => {
      if (!acc.has(plot.plotType)) {
        acc.set(plot.plotType, []);
      }

      acc.get(plot.plotType)!.push(plot);
      return acc;
    }, new Map<string, IPlotItem[]>());
  }, [plots]);
  const plotGroups = useMemo<PlotGroup[]>(
    () =>
      Array.from(groupedPlots.entries())
        .sort(([typeA], [typeB]) => typeA.localeCompare(typeB))
        .map(([plotType, grouped]) => ({
          count: grouped.length,
          plot: grouped[0],
          plotType,
        })),
    [groupedPlots],
  );

  const [selectedPlotType, setSelectedPlotType] = useState<string>();

  useEffect(() => {
    if (!plotGroups.length) {
      setSelectedPlotType(undefined);
      return;
    }

    setSelectedPlotType((current) =>
      current && groupedPlots.has(current) ? current : plotGroups[0].plotType,
    );
  }, [groupedPlots, plotGroups]);

  const selectedPlot =
    (selectedPlotType && groupedPlots.get(selectedPlotType)?.[0]) ??
    plotGroups[0]?.plot;

  return {
    plotGroups,
    selectedPlot,
    selectedPlotType,
    setSelectedPlotType,
  };
};

const PlotTypeTabs = ({
  plotGroups,
  selectedPlotType,
  setSelectedPlotType,
}: {
  plotGroups: PlotGroup[];
  selectedPlotType?: string;
  setSelectedPlotType: (plotType: string) => void;
}) => {
  if (!plotGroups.length) return null;

  return (
    <div className="min-w-0 flex-1 overflow-x-auto">
      <div className="ml-auto inline-flex min-w-max items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
        {plotGroups.map(({ count, plotType }) => {
          const isSelected = plotType === selectedPlotType;

          return (
            <button
              className={`cursor-pointer rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                isSelected
                  ? "focusoc-theme-surface shadow-sm"
                  : "text-slate-600 hover:bg-white hover:text-slate-950"
              }`}
              key={plotType}
              onClick={() => setSelectedPlotType(plotType)}
              type="button"
            >
              {formatPlotType(plotType)}
              {count > 1 && (
                <span className="ml-2 text-xs opacity-70">{count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const PlotViewerCard = ({
  collapsedClassName,
  isFullscreen,
  onToggleFullscreen,
  plots,
}: PlotViewerCardProps) => {
  const { plotGroups, selectedPlot, selectedPlotType, setSelectedPlotType } =
    usePlotSelection(plots);

  return (
    <FullscreenPanelCard
      badge={`${plots.length} plots`}
      collapsedClassName={collapsedClassName}
      controls={
        <PlotTypeTabs
          plotGroups={plotGroups}
          selectedPlotType={selectedPlotType}
          setSelectedPlotType={setSelectedPlotType}
        />
      }
      fullscreenLabel="plot"
      isFullscreen={isFullscreen}
      onToggleFullscreen={onToggleFullscreen}
      title="Plots"
      titleClassName="min-w-0 shrink-0"
    >
      {selectedPlot ? (
        <PlotItem item={selectedPlot} />
      ) : (
        <div className="flex h-full items-center justify-center text-sm font-medium text-slate-500">
          No plots available
        </div>
      )}
    </FullscreenPanelCard>
  );
};
